import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../auth/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ArrowLeft, Sparkles, CheckCircle2, Users } from "lucide-react";
import SmartBackLink from "../components/SmartBackLink";
import { toast } from "sonner";
import { track } from "../lib/analytics";

export default function BetaPage() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [why, setWhy] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [spot, setSpot] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Become an Ascend Higher beta tester";
    track("beta_page_view");
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Valid email please.");
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/beta/signup`, {
        email,
        name,
        why,
        referrer: typeof document !== "undefined" ? document.referrer : null,
      });
      setSubmitted(true);
      setSpot(r.data?.spot_in_line || null);
      track("beta_signup_completed", { email_domain: email.split("@")[1] });
    } catch (err) {
      toast.error("Couldn't submit — try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#f7f3ea] flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <div className="w-14 h-14 rounded-full bg-[#e9f0ec] mx-auto mb-5 flex items-center justify-center">
            <CheckCircle2 size={26} className="text-[#3e6b52]" />
          </div>
          <h1 className="font-display text-[36px] text-[#0f1724] tracking-tight mb-3">
            You're on the list.
          </h1>
          <p className="font-body text-[15px] text-[#4b5563] leading-relaxed mb-6">
            {spot ? `You're in line — about #${spot}. ` : ""}
            I'm onboarding 25 people at a time. You'll get a personal email from me with
            your beta access link in the next 24–48 hours. If you don't see it, check spam
            for "Ascend Higher".
          </p>
          <p className="font-body text-[13px] text-[#6b7280] mb-8 italic">
            "I built this because I needed it. Thanks for trusting me with your time." — Eric, Founder
          </p>
          <Link to="/">
            <Button className="h-11 px-6 rounded-full bg-[#486a8f] hover:bg-[#3a5a7c] text-white">
              Back home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f3ea]" data-testid="beta-page">
      <header className="px-6 py-6 max-w-4xl mx-auto flex items-center justify-between">
        <SmartBackLink />
        <div className="flex items-center gap-1">
          <span className="font-display text-[20px] font-semibold text-[#0f1724]">ascend</span>
          <span className="font-display text-[20px] font-semibold text-[#486a8f]">higher</span>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 pb-24">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#e6dcc6] mb-5">
            <Users size={12} className="text-[#3e6b52]" />
            <span className="font-mono-eyebrow text-[#3e6b52]">FOUNDER LETTER · BETA INVITE</span>
          </div>
          <h1 className="font-display text-[40px] md:text-[54px] leading-[1.05] text-[#0f1724] tracking-tight">
            I'm looking for{" "}
            <span className="italic font-light text-[#486a8f]">25 people.</span>
          </h1>
        </div>

        {/* Founder letter */}
        <div className="rounded-2xl bg-white border border-[#eadfc9] p-7 md:p-10 mb-8">
          <p className="font-body text-[15px] text-[#2b3442] leading-[1.75] whitespace-pre-line">
{`Hi — I'm Eric.

I'm looking for 25 people who feel stuck, scattered, overwhelmed, or ready to rebuild a piece of their life. Doesn't matter if it's money, health, your schedule, your relationships, your spirit, or just the feeling that you're winging everything.

I built Ascend Higher to turn scattered life into a daily roadmap. It's not a journal, not a habit tracker, not another productivity app. It's an AI life mentor that builds you a personal plan across five pillars and remembers every conversation you have with it.

I'm giving 25 people free early access in exchange for honest feedback. Tell me what's clear, what's confusing, what's missing. That's it.

Three things this is NOT:
  • Therapy or medical care
  • A guarantee
  • A polished, finished thing — you'll see rough edges and I'll fix them with you

If that sounds like your kind of thing, drop your email and one line about what's making you feel scattered. I read every reply personally.

— Eric`}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={submit} className="rounded-2xl bg-white border border-[#eadfc9] p-7 md:p-9 space-y-4">
          <div>
            <label className="font-mono-eyebrow text-[#486a8f] block mb-2">YOUR EMAIL</label>
            <Input
              data-testid="beta-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="h-11 text-[14px]"
            />
          </div>
          <div>
            <label className="font-mono-eyebrow text-[#486a8f] block mb-2">FIRST NAME (OPTIONAL)</label>
            <Input
              data-testid="beta-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="What should I call you?"
              className="h-11 text-[14px]"
            />
          </div>
          <div>
            <label className="font-mono-eyebrow text-[#486a8f] block mb-2">
              ONE LINE — WHAT'S MAKING YOU FEEL SCATTERED?
            </label>
            <textarea
              data-testid="beta-why-input"
              value={why}
              onChange={(e) => setWhy(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="One sentence is fine. Be honest — I read every reply."
              className="w-full rounded-md border border-[#e6dcc6] bg-white p-3 text-[14px] font-body text-[#2b3442] focus:outline-none focus:ring-2 focus:ring-[#486a8f]/30 resize-none"
            />
            <p className="font-body text-[11px] text-[#9aa3ae] mt-1.5">
              {why.length}/500 — optional but it helps me prioritize.
            </p>
          </div>
          <Button
            data-testid="beta-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full h-12 rounded-full bg-[#486a8f] hover:bg-[#3a5a7c] text-white font-medium text-[15px]"
          >
            {loading ? "Sending…" : "Request beta access"}
          </Button>
          <p className="font-body text-[11px] text-[#6b7280] text-center leading-relaxed">
            Ascend Higher is a personal growth and organization tool. It is not therapy,
            medical care, diagnosis, treatment, or crisis support. Crisis? Call/text 988 (US)
            or contact a licensed professional.
          </p>
        </form>

        <div className="mt-8 text-center">
          <p className="font-body text-[13px] text-[#6b7280] inline-flex items-center gap-2">
            <Sparkles size={12} className="text-[#486a8f]" />
            Or skip the line and start the free 3-day trial right now —{" "}
            <Link to="/start" className="text-[#486a8f] underline">trial · card required at Stripe</Link>.
          </p>
        </div>
      </main>
    </div>
  );
}
